/* eslint-disable @next/next/no-img-element */
"use client";

import clsx from "clsx";
import {
  AsYouType,
  CountryCode,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberWithError,
} from "libphonenumber-js";
import { ChevronDown, Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Control, Controller, Path, useWatch } from "react-hook-form";

import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

export const isValidPhoneNumber = (value: string) => {
  if (!value) {
    return false;
  }

  try {
    const phoneNumber = parsePhoneNumberWithError(value);
    const isValid = phoneNumber?.isValid();

    return isValid;
  } catch (error) {
    return false;
  }
};

// Get all countries data
const allCountries = getCountries().map((country) => ({
  code: country as CountryCode,
  dialCode: `+${getCountryCallingCode(country as CountryCode)}`,
  name: new Intl.DisplayNames(["en"], { type: "region" }).of(country) || country,
}));

interface FormPhoneFieldProps<T extends Record<string, any>> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  onCountryChange?: (country: string) => void;
  [key: string]: any;
}

export default function FormPhoneField<T extends Record<string, any>>({
  control,
  name,
  label,
  required,
  placeholder,
  disabled,
  onCountryChange,
  className = "",
  ...props
}: FormPhoneFieldProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(allCountries.find((e) => e.code === "AE")!);

  const dropdownRef = useRef<HTMLDivElement>(null);
  // Watch external value from react-hook-form to auto-detect country on prefills/defaultValues
  const watchedValue = useWatch({ control, name });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is outside both the container and dropdown
      const isOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(event.target as Node);

      if (isOpen && isOutsideDropdown) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Auto-detect and update selected country when value changes externally (e.g., defaultValues)
  useEffect(() => {
    if (typeof watchedValue !== "string") return;
    const incoming = watchedValue.trim();
    if (!incoming || !incoming.startsWith("+")) return;

    const digits = incoming.replace(/\D/g, "");
    const matchedCountry = allCountries.find((c) => digits.startsWith(c.dialCode.replace(/\D/g, "")));
    if (!matchedCountry) return;

    const dialDigits = matchedCountry.dialCode.replace(/\D/g, "");
    const localDigits = digits.slice(dialDigits.length);
    // Do not auto-detect when only the dial code is present (no local digits)
    if (localDigits.length === 0) return;

    // Prefer robust parsing; fall back to matched dial code if parsing fails
    let detectedCode: CountryCode = matchedCountry.code;
    try {
      const parsed = parsePhoneNumberWithError(incoming);
      const parsedCountry = parsed.country as CountryCode | undefined;
      detectedCode = parsedCountry ?? matchedCountry.code;
    } catch {
      detectedCode = matchedCountry.code;
    }

    if (detectedCode !== selectedCountry.code) {
      const countryData = allCountries.find((c) => c.code === detectedCode) || matchedCountry;
      setSelectedCountry(countryData);
    }
  }, [watchedValue, selectedCountry.code]);

  // Filter countries based on search
  const filteredCountries = useMemo(() => {
    const searchLower = search.toLowerCase();
    return allCountries.filter(
      (country) =>
        country.name.toLowerCase().includes(searchLower) ||
        country.dialCode.includes(searchLower) ||
        country.code.toLowerCase().includes(searchLower),
    );
  }, [search]);

  // Format and validate phone number
  const formatPhoneNumber = (value: string, country: CountryCode) => {
    const formatter = new AsYouType(country);
    return formatter.input(value);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value = "", onChange, onBlur, ...fieldProps }, fieldState: { error } }) => (
        <div className="w-full" dir="ltr">
          {label && (
            <label className="inter mb-1 block text-sm font-semibold text-[#000000]">
              {label}
              {required && <span className="me-1 text-primary">*</span>}
            </label>
          )}
          <div className="relative">
            {/* Wrapper for combined border effect */}
            <div
              className={clsx(
                "relative flex rounded-2xl",
                "border transition-all duration-300",
                error
                  ? "border-primary ring-1 ring-primary"
                  : "border-secondary-light focus-within:border-grey-border focus-within:ring-1 focus-within:ring-grey-border hover:border-grey-border",
              )}
            >
              {/* Country selector */}
              <button
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                className={clsx(
                  "flex items-center gap-2 rounded-l-2xl bg-secondary-light px-3",
                  "inter min-h-[42px] text-sm font-medium text-gray-800",
                  disabled && "cursor-not-allowed bg-gray-100 opacity-75",
                )}
              >
                <img
                  src={`https://flagcdn.com/w20/${selectedCountry.code.toLowerCase()}.png`}
                  alt={selectedCountry.name}
                  className="h-4 w-6 object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
                <span>{selectedCountry.dialCode}</span>
                <ChevronDown className="h-4 w-4" />
              </button>

              {/* Phone number input */}
              <input
                {...fieldProps}
                value={value ? value.replace(selectedCountry.dialCode, "") : ""}
                onChange={(e) => {
                  let formatted = formatPhoneNumber(e.target.value, selectedCountry.code);
                  if (formatted.startsWith("0")) {
                    formatted = formatted.slice(1);
                  }

                  const fullNumber = formatted ? `${selectedCountry.dialCode}${formatted.replace(/\D/g, "")}` : "";
                  onChange(fullNumber);
                }}
                onBlur={onBlur}
                type="tel"
                placeholder={required ? `${placeholder} *` : placeholder}
                disabled={disabled}
                dir="ltr"
                className={clsx(
                  "block w-full rounded-r-2xl bg-secondary-light",
                  "min-h-[42px] px-6 py-4",
                  "inter text-sm font-medium text-gray-800",
                  "border-0 focus:outline-none",
                  disabled && "cursor-not-allowed bg-gray-100 opacity-75",
                  className,
                )}
                {...props}
              />

              {/* Country dropdown */}
              {isOpen && (
                <Card ref={dropdownRef} className="absolute left-0 top-full z-50 mt-1 w-72 bg-white">
                  <div className="p-2">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                      <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search countries..."
                        className="pl-8"
                      />
                    </div>
                  </div>
                  <div className="max-h-60 overflow-auto">
                    {filteredCountries.map((country) => (
                      <button
                        key={country.code}
                        type="button"
                        onClick={() => {
                          setSelectedCountry(country);
                          setIsOpen(false);
                          // Update the full number with new country code
                          const digitsOnly = (value || "").replace(/\D/g, "");
                          const oldDialDigits = selectedCountry.dialCode.replace(/\D/g, "");
                          const localDigits = digitsOnly.startsWith(oldDialDigits)
                            ? digitsOnly.slice(oldDialDigits.length)
                            : digitsOnly;
                          const newFullNumber = localDigits ? `${country.dialCode}${localDigits}` : country.dialCode;
                          onChange(newFullNumber);
                          onCountryChange?.(country.code);
                        }}
                        className={clsx(
                          "flex w-full items-center gap-3 px-4 py-2",
                          "hover:bg-gray-50",
                          "inter text-sm text-gray-800",
                        )}
                      >
                        <img
                          src={`https://flagcdn.com/w20/${country.code.toLowerCase()}.png`}
                          alt={country.name}
                          className="h-4 w-6 object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                        <span>{country.name}</span>
                        <span className="ml-auto text-gray-500">{country.dialCode}</span>
                      </button>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          </div>
          {error && <div className="mt-1 text-sm font-semibold text-primary rtl:text-right">{error.message}</div>}
        </div>
      )}
    />
  );
}
